import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  AlertTriangle, 
  CheckCircle, 
  Cpu, 
  Database, 
  HardDrive, 
  ChevronRight,
  Clock,
  Calendar,
  RotateCcw,
  Trash2
} from 'lucide-react';
import { supabase } from '../../../lib/supabaseClient';
import { logActivity } from '../../../utils/activityLogger';
import { calculateEOLDate, getEOLStatus } from '../../../utils/assetUtils';
import { formatCurrency, formatDate } from '../../../utils/formatters';
import { usePreferences } from '../../../hooks/usePreferences';
import Button from '../../../components/ui/Button';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

const WriteOffReviewPanel = ({ asset, isOpen, onClose, onApprove, onSuccess }) => {
    const [isProcessing, setIsProcessing] = useState(false);
    const { t } = useTranslation();
    const { user: authUser } = useSelector((state) => state.auth);
    const userId = authUser?.id;
    const { preferences } = usePreferences();
    const preferredFormat = preferences?.date_format || 'DD/MM/YYYY';

    if (!asset) return null;

    const eolDate = calculateEOLDate(asset.purchase_date, asset.lifespan_years || (asset.lifespan_months / 12));
    const eolStatus = getEOLStatus(eolDate);
    const isExpired = eolStatus.status === 'Expired';

    const handleApprove = () => {
        if (onApprove) {
            onApprove(asset);
        }
    };

    const handleReject = async () => {
        setIsProcessing(true);
        try {
            const { error: updateError } = await supabase
                .from('assets')
                .update({ 
                    status: 'Available'
                })
                .eq('asset_tag', asset.asset_tag);

            if (updateError) throw updateError;

            await logActivity(
                'asset_restored',
                t('write_offs.review_rejected_activity'),
                asset.asset_tag,
                userId,
                {
                    reason: t('write_offs.reviewer_rejected_request'),
                    previous_status: asset.status,
                    authorized_by: authUser?.email || userId
                }
            );

            onSuccess?.(t('write_offs.asset_returned_to_inventory', { asset_tag: asset.asset_tag }));
            onClose();
        } catch (error) {
            console.error('Rejection Error:', error);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[110]"
                    />

                    {/* Side Panel */}
                    <motion.div
                        initial={{ x: '100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        className="fixed right-0 top-0 h-full w-full max-w-md bg-card border-l border-border shadow-2xl z-[120] flex flex-col"
                    >
                        {/* Header */}
                        <div className="p-6 border-b border-border flex items-center justify-between bg-muted/20">
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <h2 className="text-xl font-bold text-foreground">{asset.asset_tag}</h2>
                                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-warning/10 text-warning border border-warning/20 uppercase tracking-widest">
                                        {t('write_offs.pending_review')}
                                    </span>
                                </div>
                                <p className="text-sm text-muted-foreground">{asset.product_name}</p>
                            </div>
                            <button 
                                onClick={onClose}
                                className="p-2 hover:bg-muted rounded-full transition-colors"
                            >
                                <X className="h-5 w-5 text-muted-foreground" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6 space-y-8">
                            {/* Section 1: Justification */}
                            <section className="space-y-4">
                                    <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-widest flex items-center gap-2">
                                    <AlertTriangle className="h-3 w-3" /> {t('write_offs.disposal_justification')}
                                </h3>
                                <div className="grid grid-cols-1 gap-3">
                                    <div className="p-4 rounded-xl bg-muted/30 border border-border space-y-3">
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-muted-foreground">{t('write_offs.reason_for_review')}</span>
                                            <span className="text-sm font-bold text-error">
                                                {asset.status === 'broken' ? t('write_offs.hardware_failure') : t('write_offs.eol_expired')}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-muted-foreground flex items-center gap-2">
                                                <Calendar className="h-4 w-4" /> {t('write_offs.expected_eol_date')}
                                            </span>
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-medium">
                                                    {eolDate ? formatDate(eolDate, preferredFormat) : t('write_offs.na')}
                                                </span>
                                                {isExpired && (
                                                    <div className="p-1 bg-success/10 text-success rounded-full" title={t('write_offs.eligible_for_retirement')}>
                                                        <CheckCircle className="h-4 w-4" />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </section>

                            {/* Section 2: Hardware Profile */}
                            <section className="space-y-4">
                                <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-widest flex items-center gap-2">
                                    <Cpu className="h-3 w-3" /> {t('write_offs.hardware_profile')}
                                </h3>
                                <div className="grid grid-cols-1 gap-2">
                                    {asset.technical_specs?.processor && (
                                        <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border/50">
                                            <div className="p-2 bg-background rounded-md shadow-sm">
                                                <Cpu className="h-4 w-4 text-primary" />
                                            </div>
                                            <div>
                                                <p className="text-[10px] text-muted-foreground uppercase font-bold tracking-tighter">{t('write_offs.processor')}</p>
                                                <p className="text-sm font-medium">{asset.technical_specs.processor}</p>
                                            </div>
                                        </div>
                                    )}
                                    {asset.technical_specs?.memory && (
                                        <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border/50">
                                            <div className="p-2 bg-background rounded-md shadow-sm">
                                                <Database className="h-4 w-4 text-primary" />
                                            </div>
                                            <div>
                                                <p className="text-[10px] text-muted-foreground uppercase font-bold tracking-tighter">{t('write_offs.memory')}</p>
                                                <p className="text-sm font-medium">{asset.technical_specs.memory}</p>
                                            </div>
                                        </div>
                                    )}
                                    {asset.technical_specs?.storage && (
                                        <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border/50">
                                            <div className="p-2 bg-background rounded-md shadow-sm">
                                                <HardDrive className="h-4 w-4 text-primary" />
                                            </div>
                                            <div>
                                                <p className="text-[10px] text-muted-foreground uppercase font-bold tracking-tighter">{t('write_offs.storage')}</p>
                                                <p className="text-sm font-medium">{asset.technical_specs.storage}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </section>

                            {/* Info Callout */}
                            <div className="p-4 rounded-xl bg-primary/10 dark:bg-blue-900/10 border border-primary/20 dark:border-blue-900/20">
                                <div className="flex gap-3">
                                    <Clock className="h-5 w-5 text-blue-500 shrink-0" />
                                    <p className="text-xs text-primary dark:text-blue-300 leading-relaxed">
                                        {t('write_offs.review_callout')}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Footer Actions */}
                        <div className="p-6 border-t border-border grid grid-cols-1 gap-3 bg-muted/10">
                            <Button 
                                variant="primary" 
                                className="w-full bg-success hover:bg-success/90 border-none h-12 text-base shadow-lg shadow-success/20"
                                iconName="Trash2"
                                onClick={handleApprove}
                                loading={isProcessing}
                            >
                                {t('write_offs.approve_disposal')}
                            </Button>
                            <Button 
                                variant="outline" 
                                className="w-full border-error/20 text-error hover:bg-error/5 h-12 text-base"
                                iconName="RotateCcw"
                                onClick={handleReject}
                                loading={isProcessing}
                                disabled={isProcessing}
                            >
                                {t('write_offs.reject_return_inventory')}
                            </Button>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default WriteOffReviewPanel;
